/**
 * T263 — Quick Add Players modal.
 *
 * Usage: <QuickAddPlayersModal onClose={...} onCreated={(players) => ...} />
 *
 * Bulk paste textarea. Each line becomes one Player row. Accepted
 * shapes (matches backend _LINE_RE):
 *
 *   Suresh K
 *   Suresh K, 7
 *   Suresh K - 7
 *   Suresh K #7
 *   7 - Suresh K
 *
 * Posts to POST /api/coredata/players/quick-create/. The created
 * rows return with a claim_url the scorer can share with the player
 * later (they can claim → all stats accumulate against their
 * canonical profile).
 */
import { useEffect, useRef, useState } from "react";
import { AlertCircle, Copy, Plus, X } from "lucide-react";

import { quickCreatePlayers, findPlayerByMobile } from "../../api/coredata.api";
import useDialogDismiss from "../../hooks/useDialogDismiss";


export default function QuickAddPlayersModal({
  onClose,
  onCreated,
  organizationId = null,
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null); // null = form, [{...}] = success
  const textareaRef = useRef(null);
  const panelRef = useRef(null);
  const safeClose = busy ? () => {} : (onClose || (() => {}));
  useDialogDismiss({ open: true, onClose: safeClose, ref: panelRef });

  // 2026-05-30 — mobile search (companion to Player self-signup). The
  // scorer can find a self-signed-up player by their mobile and attach
  // them to the current team without creating a duplicate row.
  const [searchMobile, setSearchMobile] = useState("");
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchHit, setSearchHit] = useState(null);     // {id, first_name, ...} or null
  const [searchMiss, setSearchMiss] = useState(false);  // true when last search returned 404
  const [searchError, setSearchError] = useState("");

  async function runMobileSearch() {
    setSearchError(""); setSearchHit(null); setSearchMiss(false);
    const clean = searchMobile.replace(/\s+/g, "");
    if (clean.length < 10) {
      setSearchError("Enter at least 10 digits.");
      return;
    }
    setSearchBusy(true);
    try {
      const { data } = await findPlayerByMobile(clean);
      setSearchHit(data);
    } catch (e) {
      if (e?.response?.status === 404) {
        setSearchMiss(true);
      } else {
        setSearchError(e?.response?.data?.detail || "Lookup failed.");
      }
    } finally {
      setSearchBusy(false);
    }
  }

  function attachFoundPlayer() {
    if (!searchHit) return;
    // Reuse the same shape the bulk-create flow returns so the parent's
    // onCreated handler can splice the row into the team without
    // branching. claim_url isn't relevant here (already claimed), so we
    // pass an empty string.
    const row = {
      id: searchHit.id,
      first_name: searchHit.first_name,
      last_name: searchHit.last_name,
      jersey_number: searchHit.jersey_number || "",
      is_quick_create: !!searchHit.is_quick_create,
      claim_token: "",
      claim_url: "",
    };
    if (typeof onCreated === "function") onCreated([row]);
    onClose && onClose();
  }

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  async function submit() {
    setError("");
    if (lines.length === 0) {
      setError("Paste at least one name.");
      return;
    }
    if (lines.length > 50) {
      setError(`Too many (max 50 per request). You have ${lines.length}.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await quickCreatePlayers({
        lines, organizationId,
      });
      setCreated(data.players || []);
      if (typeof onCreated === "function") onCreated(data.players);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
        "Failed to create players. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  function copyShareLinks() {
    if (!created) return;
    const text = created
      .map((p) => {
        const name = [p.first_name, p.last_name].filter(Boolean).join(" ");
        return `${name}${p.jersey_number ? ` (${p.jersey_number})` : ""}: ${p.claim_url}`;
      })
      .join("\n");
    navigator.clipboard.writeText(text).then(
      () => { /* toast handled by parent */ },
      () => {},
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Quick add players"
        className="max-w-xl w-full rounded-lg bg-white p-5 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Plus className="w-5 h-5" /> Quick add players
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Paste names (one per line). Match starts faster — you can
              fill in details later.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {created === null ? (
          <>
            {/* 2026-05-30 — Mobile lookup. Scorer types the player's
                mobile; if the player has self-signed-up (or a row
                already exists with that mobile), we attach the
                existing Player instead of creating a duplicate. */}
            <div className="mb-3 p-3 rounded bg-emerald-50 border border-emerald-200">
              <div className="text-xs font-semibold text-emerald-900 mb-1.5">
                Find existing player by mobile
              </div>
              <div className="flex gap-2">
                <input
                  type="tel"
                  inputMode="tel"
                  placeholder="+91 98765 43210"
                  value={searchMobile}
                  onChange={(e) => {
                    setSearchMobile(e.target.value);
                    setSearchHit(null); setSearchMiss(false); setSearchError("");
                  }}
                  className="flex-1 border rounded px-2 py-1.5 text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={runMobileSearch}
                  disabled={searchBusy || !searchMobile}
                  className="px-3 py-1.5 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700 disabled:opacity-50"
                >
                  {searchBusy ? "…" : "Find"}
                </button>
              </div>
              {searchHit && (
                <div className="mt-2 p-2 bg-white border border-emerald-300 rounded flex items-center justify-between">
                  <div className="text-sm">
                    <div className="font-semibold text-gray-900">
                      {[searchHit.first_name, searchHit.last_name].filter(Boolean).join(" ") || "Unnamed player"}
                      {searchHit.is_claimed && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-700">
                          ✓ self-signed up
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {searchHit.mobile || "—"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={attachFoundPlayer}
                    className="px-3 py-1.5 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-700"
                  >
                    Attach
                  </button>
                </div>
              )}
              {searchMiss && (
                <div className="mt-2 text-[11px] text-amber-700">
                  No player found for that mobile — paste their name below to create.
                </div>
              )}
              {searchError && (
                <div className="mt-2 text-[11px] text-rose-700">{searchError}</div>
              )}
            </div>

            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={10}
              placeholder={"Suresh K, 7\nP Ravichandran\nMukesh Kumar, 11, mukesh@example.com\nAkash #4, +91 98xxx12345"}
              className="w-full border rounded px-2 py-2 font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              {lines.length}/50 lines · accepted shapes:
              <code className="ml-1 bg-gray-100 px-1 rounded">Name</code>,
              <code className="ml-1 bg-gray-100 px-1 rounded">Name, 7</code>,
              <code className="ml-1 bg-gray-100 px-1 rounded">Name, 7, email</code>,
              <code className="ml-1 bg-gray-100 px-1 rounded">Name, 7, phone</code>
            </p>
            <p className="text-[11px] text-amber-700 mt-1">
              💡 Add an email after the name + jersey — we'll send the
              player a claim link automatically so they can own their stats.
            </p>

            {error && (
              <div className="flex items-start gap-2 p-2 mt-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                {error}
              </div>
            )}

            <div className="flex gap-2 justify-end mt-4">
              <button
                onClick={onClose} disabled={busy}
                className="px-4 py-2 border rounded text-sm hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={submit} disabled={busy || lines.length === 0}
                className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">
                {busy ? "Adding…" : `Add ${lines.length} player${lines.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="rounded border border-green-200 bg-green-50 p-3 mb-3 text-sm">
              <strong className="text-green-900">
                Added {created.length} player{created.length === 1 ? "" : "s"}.
              </strong>
              <p className="text-green-800 text-xs mt-1">
                You can start scoring immediately. Each player got a
                claim link — share with the player later so they can
                build a profile and own their stats.
              </p>
            </div>
            <ul className="text-sm divide-y border rounded max-h-60 overflow-y-auto">
              {created.map((p) => {
                const name = [p.first_name, p.last_name].filter(Boolean).join(" ");
                const label = name + (p.jersey_number ? ` (${p.jersey_number})` : "");
                return (
                  <li key={p.id} className="flex justify-between items-center px-2 py-1.5">
                    <span>{label}</span>
                    <a href={p.claim_url} target="_blank" rel="noreferrer"
                       className="text-xs text-blue-600 hover:underline">claim link</a>
                  </li>
                );
              })}
            </ul>
            <div className="flex gap-2 justify-end mt-3">
              <button
                onClick={copyShareLinks}
                className="inline-flex items-center gap-1 px-3 py-1.5 border rounded text-sm hover:bg-gray-50">
                <Copy className="w-3.5 h-3.5" />
                Copy claim links
              </button>
              <button
                onClick={onClose}
                className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
